import { useState, useEffect, useCallback, useRef } from "react";

/**
 * useSpeechSynthesis (JS)
 * Female voice preferred
 */
export const useSpeechSynthesis = (options = {}) => {
  const {
    onStart,
    onEnd,
    onPause,
    onResume,
    onBoundary,
    onError,
  } = options;

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [currentText, setCurrentText] = useState("");

  const utteranceRef = useRef(null);

  const isSupported =
    typeof window !== "undefined" && "speechSynthesis" in window;

  /* --------------------------------------------------
     LOAD VOICES (FEMALE FIRST)
  -------------------------------------------------- */
  useEffect(() => {
    if (!isSupported) return;

    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      setVoices(availableVoices);

      if (selectedVoice) return;

      // 🎯 Prefer female English voices
      const femaleVoice = availableVoices.find((voice) => {
        const name = voice.name.toLowerCase();
        return (
          voice.lang.startsWith("en") &&
          (
            name.includes("female") ||
            name.includes("samantha") ||
            name.includes("victoria") ||
            name.includes("zira") ||
            name.includes("karen") ||
            name.includes("moira") ||
            name.includes("tessa") ||
            name.includes("fiona") ||
            name.includes("google uk english female") ||
            name.includes("google us english")
          )
        );
      });

      if (femaleVoice) {
        setSelectedVoice(femaleVoice);
      } else {
        // fallback → any English voice
        const englishVoice = availableVoices.find(v =>
          v.lang.startsWith("en")
        );
        setSelectedVoice(englishVoice || availableVoices[0] || null);
      }
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [isSupported, selectedVoice]);

  /* --------------------------------------------------
     SPEAK
  -------------------------------------------------- */
  const speak = useCallback(
    (text) => {
      if (!isSupported) {
        onError?.("Speech synthesis not supported");
        return;
      }

      if (!text) return;

      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utteranceRef.current = utterance;

      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }

      utterance.rate = rate;
      utterance.pitch = pitch;
      utterance.volume = 1;

      utterance.onstart = () => {
        setIsSpeaking(true);
        setIsPaused(false);
        setCurrentText(text);
        onStart?.();
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        setIsPaused(false);
        setCurrentText("");
        onEnd?.();
      };

      utterance.onpause = () => {
        setIsPaused(true);
        onPause?.();
      };

      utterance.onresume = () => {
        setIsPaused(false);
        onResume?.();
      };

      utterance.onboundary = (event) => {
        onBoundary?.(event.charIndex);
      };

      utterance.onerror = (event) => {
        setIsSpeaking(false);
        setIsPaused(false);
        onError?.(event.error || "Speech error");
      };

      window.speechSynthesis.speak(utterance);
    },
    [isSupported, selectedVoice, rate, pitch, onStart, onEnd, onPause, onResume, onBoundary, onError]
  );

  /* --------------------------------------------------
     CONTROLS
  -------------------------------------------------- */
  const pause = useCallback(() => {
    if (isSupported && isSpeaking) {
      window.speechSynthesis.pause();
    }
  }, [isSupported, isSpeaking]);

  const resume = useCallback(() => {
    if (isSupported && isPaused) {
      window.speechSynthesis.resume();
    }
  }, [isSupported, isPaused]);

  const cancel = useCallback(() => {
    if (isSupported) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      setIsPaused(false);
      setCurrentText("");
    }
  }, [isSupported]);

  /* --------------------------------------------------
     CLEANUP
  -------------------------------------------------- */
  useEffect(() => {
    return () => {
      if (isSupported) {
        window.speechSynthesis.cancel();
      }
    };
  }, [isSupported]);

  return {
    speak,
    pause,
    resume,
    cancel,
    isSpeaking,
    isPaused,
    isSupported,
    voices,
    selectedVoice,
    setSelectedVoice,
    rate,
    setRate,
    pitch,
    setPitch,
    currentText,
  };
};
